<template>
    <div class="components-content__table">
        <hc-table
            ref="table"
            v-loading="loading"
            :max-height="tableHeight"
            stripe
            :data="componentsData"
            @selection-change="handleSelectionChange"
        >
            <hc-table-column
                v-if="isAlreadyUse"
                type="selection"
                :selectable="selectable"
                width="55"
            />
            <hc-table-column type="index" label="序号" width="80" />
            <hc-table-column prop="name" label="组件包名称" width="300" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                    <div :key="scope.row.id" class="component-item__name">
                        <span class="component-item__name--folder" />
                        <span class="ellipsis">{{ scope.row.name }}</span>
                        <span v-if="scope.row.isSystem" class="component-item__name--system">系统</span>
                        <span v-if="scope.row.isShare" class="component-item__name--share">共享</span>
                        <hc-popover
                            trigger="hover"
                            :offset="100"
                            popper-class="component-dependencies"
                            placement="right"
                        >
                            <h4 class="component-dependencies__title">
                                依赖的组件包及版本
                            </h4>
                            <ul v-if="getCurrentInfo(scope.row).internalDependencies" class="component-dependencies__content">
                                <li
                                    v-for="item in getCurrentInfo(scope.row).internalDependencies"
                                    :key="item.name"
                                    class="component-dependencies__content__item"
                                >
                                    {{ item.name }}({{ item.version }})
                                </li>
                            </ul>
                            <span
                                v-if="getCurrentInfo(scope.row).internalDependencies && getCurrentInfo(scope.row).internalDependencies.length"
                                slot="reference"
                                class="component-item__name--dependencies"
                            />
                        </hc-popover>
                    </div>
                </template>
            </hc-table-column>
            <hc-table-column prop="latestVersion" label="引用版本">
                <template slot-scope="scope">
                    <div style="position: relative">
                        <div class="icon-box"
                            :class="{
                                useless: !canSelected(scope.row),
                                update: canUpgrade(scope.row),
                                both: canUpgrade(scope.row) && !canSelected(scope.row),
                            }"
                        >
                            <hc-tooltip
                                :key="scope.row.id"
                                class="item"
                                effect="dark"
                                :content="`该组件包${getCurrentInfo(scope.row).version}版本与当前工作区引用的引擎版本不兼容！请更改该组件包版本`"
                                placement="top"
                            >
                                <span v-if="!canSelected(scope.row)" class="component-item__name--useless" />
                            </hc-tooltip>
                            <span v-if="canUpgrade(scope.row)" class="component-item__name--update" />
                        </div>
                        <span>{{ getCurrentInfo(scope.row).version }}</span>
                        <hc-button v-if="canChange(scope.row)" class="components-content__table__alter" type="text" @click="alterQuotesVersion(scope.row)">
                            更改
                        </hc-button>
                    </div>
                </template>
            </hc-table-column>
            <hc-table-column prop="updatedAt" label="引擎范围" width="180">
                <template slot-scope="scope">
                    <span>
                        {{ getCurrentInfo(scope.row).minEngineVersion }} ~
                        {{ getCurrentInfo(scope.row).maxEngineVersion === newestEngineVersion ? '最新版本' : getCurrentInfo(scope.row).maxEngineVersion }}
                    </span>
                </template>
            </hc-table-column>
            <hc-table-column prop="description" label="备注" />
            <hc-table-column label="操作">
                <template slot-scope="{ row }">
                    <hc-button v-if="canCancel(row)" type="text" :class="{ danger: true, 'change-status': !!row.changeStatus }" @click="toggleRemove(row)">
                        {{ row.changeStatus ? '已移除' : '不再引用' }}
                    </hc-button>
                    <hc-button v-else-if="canQuote(row)" type="text" :disabled="!canSelected(row)" :class="{ 'change-status': !!row.changeStatus }" @click="toggleQuote(row)">
                        {{ row.changeStatus ? '已引用' : '引用' }}
                    </hc-button>
                    <hc-button v-if="row.canDownload" type="text" @click="toDownloadComponent(row)">
                        导出
                    </hc-button>
                </template>
            </hc-table-column>
        </hc-table>
        <alter-quotes-vrsion
            :version-data="componentVersionData"
            :dialog-visible="dialogVisible"
            :workspace-engine="workspaceEngine"
            @closeDialog="closeDialog"
            @save="saveAlterQuotesVersion"
        />
        <hc-dialog
            top="72px"
            width="480px"
            :title="title"
            append-to-body
            class="ct-dialog"
            :visible.sync="componentDialogVisible"
        >
            <top-tip>
                <div>
                    组件模板导出文件为zip格式，导出后将自动命名为“{{ readyToDownloadInfo.name }}.zip”，更多详情请
                    <hc-link :underline="false" type="primary" href="https://open.mingyuanyun.com/docs/mpaas/latest/open-integrate/Customcomponents" target="_blank">
                        查看文档
                    </hc-link>。
                </div>
            </top-tip>
            <hc-form>
                <hc-form-item v-for="item in componentForm" :key="item.key" :label="item.label" :label-position="'left'" label-width="88px">
                    {{ item.value }}
                </hc-form-item>
            </hc-form>
            <div slot="footer" class="dialog-footer">
                <hc-button @click="closeComponentDialog">
                    取消
                </hc-button>
                <hc-button type="primary" @click="downloadComponent">
                    确认
                </hc-button>
            </div>
        </hc-dialog>
    </div>
</template>

<script>
import semver from 'semver'

import { downloadFile } from '@common/utils/download'
import TopTip from '@common/components/common/top-tip'
import calculateTableHeight from '@/common/mixins/calculate-table-height'
import alterQuotesVrsion from './alter-quotes-version'

export default {
    name: 'ComponentTable',
    components: {
        TopTip,
        alterQuotesVrsion,
    },
    mixins: [calculateTableHeight],
    props: {
        componentsData: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        workspaceEngine: {
            type: String,
            default: '',
        },
        isAlreadyUse: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            title: '',
            dialogVisible: false,
            componentForm: [],
            componentDialogVisible: false,
            componentVersionData: {},
            newestEngineVersion: '999999.999999.999999',
            readyToDownloadInfo: {},
        }
    },
    computed: {
        isTenantApp() {
            return this.$store.state.modeling.app.isTenant
        },
    },
    methods: {
        closeComponentDialog() {
            this.componentDialogVisible = false
            this.title = ''
            this.componentForm = []
            this.readyToDownloadInfo = {}
        },
        downloadComponent() {
            const { id, versions } = this.readyToDownloadInfo
            const { version } = this.getCurrentInfo(this.readyToDownloadInfo)
            const versionId = (versions.find(item => item.version === version) || {}).id
            const url = `${window.location.origin}/api_v3/studio/workspace-component/download?packageId=${id}&versionId=${versionId}`

            downloadFile(url)
            this.closeComponentDialog()
        },
        toDownloadComponent(row) {
            this.componentDialogVisible = true
            const { description, versions } = row
            const { version } = this.getCurrentInfo(row)
            this.readyToDownloadInfo = row

            this.title = `导出组件 ${description}`
            this.componentForm = [
                {
                    key: 'version',
                    label: '版本',
                    value: version,
                },
                {
                    key: 'publishtime',
                    label: '发布时间',
                    value: (versions.find(item => item.version === version) || {}).updatedAt,
                },
            ]
        },
        canCancel(component) {
            const { isSystem, isTenant } = component
            const { isAlreadyUse } = this
            if (isSystem) {
                return false
            }
            if (this.isTenantApp) {
                return isAlreadyUse && isTenant
            }
            return isAlreadyUse
        },
        canQuote(component) {
            const { isTenant } = component
            const { isAlreadyUse } = this
            if (this.isTenantApp) {
                return !isAlreadyUse && isTenant
            }
            return !isAlreadyUse
        },
        canChange(component) {
            const { canChange, isTenant } = component
            if (this.isTenantApp) {
                return canChange && isTenant
            }
            return canChange
        },
        canUpgrade(component) {
            const {
                latestVersion,
                used,
                version,
                isSystem,
            } = component
            if (isSystem) return this.canChange(component)
            const canUpgrade = used ? !semver.gte(used.version, latestVersion) : !semver.gte(version, latestVersion)

            if (this.isTenantApp) {
                return used ? this.canCancel(component) && canUpgrade : this.canQuote(component) && canUpgrade
            }
            return canUpgrade
        },
        handleSelectionChange(params) {
            this.$emit('select-change', params)
        },
        getCurrentInfo(item) {
            return item.used || item
        },
        selectable(item) {
            if (this.isTenantApp && this.isAlreadyUse) {
                return this.canCancel(item)
            }
            if (this.isAlreadyUse) return true
            return this.canSelected(item)
        },
        canSelected(item) {
            const currentItem = this.getCurrentInfo(item)

            const engineVersion = this.workspaceEngine
            const { minEngineVersion, maxEngineVersion } = currentItem
            // const deps = currnetItem.dependencies
            // const depCanSelected = deps ? deps.every(dep => this.canSelected(dep)) : true

            if (engineVersion === 'nightly' && currentItem.canDebug) return true

            return  semver.valid(engineVersion)
              && semver.valid(minEngineVersion)
              && semver.valid(maxEngineVersion)
              && semver.gte(engineVersion, minEngineVersion)
              && semver.lte(engineVersion, maxEngineVersion)
        },
        clearSelection() {
            this.$refs.table && this.$refs.table.clearSelection()
        },
        alterQuotesVersion(params) {
            this.dialogVisible = true
            this.componentVersionData = params
        },
        closeDialog() {
            this.dialogVisible = false
        },
        saveAlterQuotesVersion(params) {
            this.closeDialog()
            const alteredIndex = this.componentsData.findIndex(item => item.id === this.componentVersionData.id)
            this.$emit('alter-current-component-version', {
                alteredIndex,
                alteredData: params,
            })
        },
        toggleRemove(row) {
            this.$emit('cancel-quotes', row)
        },
        toggleQuote(row) {
            this.$emit('quotes-item', row)
        },
    },
}
</script>

<style lang="less" scoped>
.components-content__table {
  margin-top: 16px;
  &__alter.hc-button {
      margin-left: 6px;
  }
  .icon-box {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      &.update {
          left: -16px;
      }
      &.useless {
          left: -20px;
      }
      &.both {
          left: -35px;
      }
  }
  .change-status {
      opacity: 0.4;
  }
}
</style>
<style lang="less">
.components-content__table {
  .hc-table .cell {
      overflow: visible;
  }
}
.component-dependencies {
  &__title {
      color: #B9BFD3;
      font-size:12px;
  }
  &__content {
      margin-top: 8px;
      &__item {
          display: block;
          color: #3E4352;
          font-size: 14px;
          &:not(:last-child) {
              margin-bottom: 16px;
          }
      }
  }
}
.ct-dialog {
    .hc-form {
        margin-top: 16px;
        .hc-form-item__label {
            line-height: 20px;
            text-align: left;
            &:before {
                display: none;
            }
        }
        .hc-form-item__content {
            line-height: 20px;
        }
    }
    .dialog-footer {
        .hc-button {
            padding-left: 28px;
            padding-right: 28px;
        }
    }
}
</style>
